import { z } from "zod";
import { ProseSchema } from "./prose.js";
import { ArcDifficultySchema, FactIdSchema, StoryBeatSchema } from "./primitives.js";
import { ConsequenceSchema } from "./check-spec.js";
/** Finite predicates over admitted history. No code, expressions, or model-selected transitions. */
export const ArcConditionSchema = z.discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("fact"), id: FactIdSchema }),
    z.strictObject({ kind: z.literal("document"), id: z.string().min(1) }),
    z.strictObject({ kind: z.literal("phase"), id: z.string().min(1) }),
    z.strictObject({ kind: z.literal("signal"), id: z.string().min(1), currentTurn: z.boolean().default(false) }),
    z.strictObject({ kind: z.literal("process_signal"), id: z.string().min(1) }),
    z.strictObject({ kind: z.literal("process"), status: z.enum(["stated", "revised", "declined"]), currentTurn: z.boolean().default(false) }),
    z.strictObject({ kind: z.literal("test"), id: z.string().min(1) }),
    z.strictObject({ kind: z.literal("action"), signalId: z.string().min(1) }),
    z.strictObject({ kind: z.literal("hypothesis") }),
    z.strictObject({ kind: z.literal("recommendation") }),
    /**
     * A route on the calendar, prepared in an earlier phase. On `time_days_left` it reads the engine's
     * clock (Andre's ruling R2, 2026-10-29; engine `arcResources`): the days left COUNTING TODAY, the
     * budget less the whole days the half-days have spent, never a counter the adjudicator charges, so
     * `remainingAtMost: N` first opens on day budget − N + 1. The state block's `time_days_left` (the
     * adjudicator's input) counts only the days after today, one fewer: never set a threshold from it.
     */
    z.strictObject({ kind: z.literal("deadline"), resource: z.string().min(1), remainingAtMost: z.number().nonnegative(), preparedIn: z.string().min(1) }),
]);
/**
 * THE DEAL ON THE TABLE, PLACED IN THE STORY (Andre, 2026-09-17). A bargain is authored on
 * an arc phase instead of being improvised by the adjudicator after a COST roll: when the
 * learner is in that phase, a named person offers something real for an immediate price, and
 * the learner answers in their next message. Declining is as legitimate as accepting.
 *
 * `gains` are the whole benefit and `cost_consequences` the whole price; `offer` and `cost`
 * are that person's own spoken words, and may promise nothing the engine does not deliver.
 */
export const ArcBargainGainSchema = z.discriminatedUnion("kind", [
    /** The authored document reaches the desk clean. */
    z.strictObject({ kind: z.literal("document"), document_id: z.string().min(1) }),
    /** The person is one step more open to the player. */
    z.strictObject({ kind: z.literal("disposition"), npc: z.string().min(1) }),
]);
/**
 * A price is pressure, goodwill or a relationship; never a day (time is engine arithmetic over spent
 * moves). The schema cannot see the bible's clocks, so a CLOCK_TICK on the day clock or the
 * presentation gate passes here and is refused by case:check's N3 (check-references.ts), an ERROR
 * (the time-charge critique of 2026-10-29, finding 12: answered there, not re-built here).
 */
const ArcBargainPriceSchema = ConsequenceSchema.refine((c) => c.type === "CLOCK_TICK" || c.type === "RESOURCE_COST" && c.resource !== "time_days_left" && c.resource !== "time_days" || c.type === "NPC_DISPOSITION_SHIFT" && c.direction === "DOWN", "A bargain's price is a pressure advancing, goodwill or analysts spent, or a person one step less open");
export const ArcBargainSchema = z.strictObject({
    /** The slot. Offered at most once per session; alternative phases may share one slot with different offers. */
    id: z.string().min(1),
    offered_by: z.string().min(1),
    /** The person's own words, first person, to "you". */
    offer: ProseSchema,
    /**
     * The same offer for a learner who already holds every file it names (stage 3, CM-05). The
     * deal is still made for its other gains; `offer` would promise paper already on the desk.
     * Codex writes it; absent, the engine speaks `offer`.
     */
    offer_when_held: ProseSchema.optional(),
    /**
     * A two-paper deal made for one paper, the other already held clean: the offer for that one paper,
     * keyed by its document id (round 2 of 2026-10-30 on PR #170). One held-file line for both cases
     * either names both papers, most of its words then unspeakable beside a scene that rightly names
     * only the missing one, or promises a reading where the deal brings the paper. Codex writes it;
     * absent for the paper still given, the engine speaks `offer_when_held`, then `offer`.
     */
    offer_when_held_by: z.record(z.string().min(1), ProseSchema).optional(),
    cost: ProseSchema,
    gains: z.array(ArcBargainGainSchema).min(1).max(2),
    cost_consequences: z.array(ArcBargainPriceSchema).min(1).max(3),
});
/**
 * A RARE EMPHASIS MOMENT (ADR 0017). The scene's lighting turns from Soft Wash
 * to Hard Light when one named, already-public story event happens, and holds
 * for a few turns. Authored here, derived by the stage fold from the event
 * log, never chosen by a model and never spent from a budget: the ceiling of
 * two per case is this schema. Most cases should author one, or none.
 */
export const ArcEmphasisSchema = z.strictObject({
    id: z.string().min(1),
    /** The information release or story beat that opens the episode. */
    on: z.discriminatedUnion("kind", [
        z.strictObject({ kind: z.literal("phase"), id: z.string().min(1) }),
        z.strictObject({ kind: z.literal("fact"), id: FactIdSchema }),
        z.strictObject({ kind: z.literal("document"), id: z.string().min(1) }),
    ]),
    /** Acted turns the episode holds, counting the opening one. Free turns never close it. */
    holds_turns: z.number().int().min(1).max(4).default(2),
});
export const ARC_EMPHASIS_MAX = 2;
export const NarrativeArcSchema = z.strictObject({
    version: z.literal(1),
    id: z.string().min(1),
    revision: z.number().int().positive(),
    /** Exact natural-language declaration phrases; no regex authoring or inferred beliefs. */
    signals: z.array(z.strictObject({ id: z.string().min(1), phrases: z.array(z.string().min(3)).min(1) })),
    nodes: z.array(z.strictObject({
        id: z.string().min(1),
        purpose: z.string().min(1),
        /** Empty means entry from any phase, including a late first use. */
        from: z.array(z.string().min(1)),
        /** Earned alternatives permanently supersede this development, even after a detour. */
        blockedBy: z.array(z.string().min(1)).default([]),
        /** Alternatives of conjunctions. [[]] is unconditional. */
        when: z.array(z.array(ArcConditionSchema)).min(1),
        priority: z.number().int(),
        once: z.boolean(),
        intensity: z.enum(["quiet", "routine", "substantive", "major"]),
        /** Present-tense obligation only. Future graph is never in a model brief. */
        entry: ProseSchema,
        /** Writing direction for this admitted entry. The entry remains a speakable outage version. */
        performance: z.string().min(1).optional(),
        guidance: z.string().min(1),
        /** Explicit authoring rationale for exits, loops, refusal, and missed preparation. */
        exit: z.string().min(1),
        missedRoute: z.string().min(1),
        development: z.enum(["interpretation", "world_change"]),
        documents: z.array(z.string().min(1)),
        /** A deal offered while this phase is current (2026-09-17). */
        bargain: ArcBargainSchema.optional(),
        /**
         * THE CURVE (ADR 0020, idea 3). The phase's authored difficulty, which the engine reads
         * as a DC ceiling — easy 10, moderate 15, hard 20, very_hard 25 — and applies to the
         * Adjudicator's priced DC before the checks row is written. It only ever lowers; it is
         * never a floor, never a penalty and never a removed option. Absent means no ceiling.
         * Never shown to a model: the arc is stripped from every model view.
         */
        difficulty: ArcDifficultySchema.optional(),
        /** Whether the phase resolves toward hope or fear (Laws, Hamlet's Hit Points). Read by the debrief only. */
        beat: StoryBeatSchema.optional(),
        /**
         * What kind of turn a world change is, and which facts it makes the learner read again
         * (stage 3; idea 3 Q12). Read by the debrief, by phase id; no event carries it. There is
         * no authored sentence of what the held evidence now means: that is the learner's to say.
         */
        twist: z
            .strictObject({
            kind: z.enum(["reversal", "revelation", "betrayal", "deadline_moved", "rival", "false_victory", "false_defeat"]),
            rereads: z.array(FactIdSchema).min(1),
        })
            .optional(),
    })).min(1),
    /** Authored simulation protocols; a match yields a fixed artifact, never an improvised outcome. */
    tests: z.array(z.strictObject({
        id: z.string().min(1),
        requiresSignals: z.array(z.string().min(1)).min(1),
        requiresDocuments: z.array(z.string().min(1)),
        documentId: z.string().min(1),
        receipt: ProseSchema,
    })).default([]),
    unmodeledTest: ProseSchema.optional(),
    /** Optional, so every existing bible keeps resolving. Absent means Soft Wash throughout. */
    emphasis: z.array(ArcEmphasisSchema).max(ARC_EMPHASIS_MAX).optional(),
}).superRefine((arc, ctx) => {
    const nodes = new Set(arc.nodes.map((node) => node.id));
    const signals = new Set(arc.signals.map((signal) => signal.id));
    const issue = (message) => ctx.addIssue({ code: "custom", message });
    if (nodes.size !== arc.nodes.length || signals.size !== arc.signals.length || new Set(arc.tests.map((test) => test.id)).size !== arc.tests.length)
        issue("Arc identifiers must be unique within each collection");
    for (const node of arc.nodes) {
        for (const id of [...node.from, ...node.blockedBy])
            if (!nodes.has(id))
                issue(`Unknown phase reference ${id}`);
        for (const conditions of node.when)
            for (const c of conditions) {
                if (c.kind === "phase" && !nodes.has(c.id))
                    issue(`Unknown phase ${c.id}`);
                if (c.kind === "action" && !signals.has(c.signalId))
                    issue(`Unknown action signal ${c.signalId}`);
                if (c.kind === "test" && !arc.tests.some((test) => test.id === c.id))
                    issue(`Unknown test ${c.id}`);
                if ((c.kind === "signal" || c.kind === "process_signal") && !signals.has(c.id))
                    issue(`Unknown signal ${c.id}`);
                if (c.kind === "deadline" && (!nodes.has(c.preparedIn) || c.preparedIn === node.id))
                    issue("Deadline requires an earlier preparation phase");
            }
        if (!node.once && !node.when.every((conditions) => conditions.some((c) => (c.kind === "signal" || c.kind === "process") && c.currentTurn || c.kind === "test")))
            issue("Repeatable phases require a current-turn declaration in every route");
    }
    if (arc.tests.length > 0 && arc.unmodeledTest === undefined)
        issue("Prototype protocols need an explicit response for unmodeled proposals");
    const reachable = new Set();
    for (let pass = 0; pass < arc.nodes.length; pass += 1)
        for (const node of arc.nodes) {
            if (node.from.length > 0 && !node.from.some((id) => reachable.has(id)))
                continue;
            if (node.when.some((conditions) => conditions.every((c) => c.kind === "phase" ? reachable.has(c.id) : c.kind === "deadline" ? reachable.has(c.preparedIn) : true)))
                reachable.add(node.id);
        }
    for (const node of arc.nodes)
        if (!reachable.has(node.id))
            issue(`Phase ${node.id} has no route from an entry phase`);
    for (const test of arc.tests)
        for (const id of test.requiresSignals)
            if (!signals.has(id))
                issue(`Unknown test attribute ${id}`);
    const emphasis = arc.emphasis ?? [];
    if (new Set(emphasis.map((moment) => moment.id)).size !== emphasis.length)
        issue("Emphasis identifiers must be unique");
    if (new Set(emphasis.map((moment) => `${moment.on.kind}:${moment.on.id}`)).size !== emphasis.length)
        issue("Two emphasis moments cannot open on the same event");
    for (const moment of emphasis)
        if (moment.on.kind === "phase" && !nodes.has(moment.on.id))
            issue(`Unknown emphasis phase ${moment.on.id}`);
});
export const NarrativeDeclarationSchema = z.strictObject({
    kind: z.enum(["process", "revision", "decline", "signal", "test"]),
    key: z.string().min(1),
    /** Offsets address the exact INTENT_DECLARED text on sourceTurn. */
    sourceTurn: z.number().int().nonnegative(),
    start: z.number().int().nonnegative(),
    end: z.number().int().positive(),
    quote: z.string().min(1),
}).refine((value) => value.end - value.start === value.quote.length, "Source span length must match quote");
export const NarrativeDevelopmentSchema = z.strictObject({ turn: z.number().int().nonnegative(), text: ProseSchema });
//# sourceMappingURL=narrative-arc.js.map